import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import Block from 'shopper/components/Block';
import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Icon from 'shopper/components/Icon';
import Tag from 'shopper/components/Tag';

import Anchor from 'components/Anchor';
import Image from 'components/Image';

import { notificationQueryKeys } from 'lib/queryKeys';
import { get } from 'lib/request';

import { useDrawer } from 'providers/DrawerProvider';
import { useModal } from 'providers/ModalProvider';
import { useTopbar } from 'providers/TopbarProvider';
import { useUser } from 'providers/UserProvider';

import APP from 'constants/app';
import LINKS from 'constants/links';
import Z_INDEX_SCALE from 'constants/zIndexScale';

const UserProfileButton = dynamic(() => import('./UserProfileButton'), {
  ssr: false,
});
const LoginModal = dynamic(() => import('components/LoginModal'), {
  ssr: false,
});
const TopbarMobileDrawer = dynamic(
  () => import('components/TopbarMobileDrawer'),
  { ssr: false }
);

const StyledNotificationTag = styled(Tag)`
  cursor: pointer;
`;
const StyledTopbarMain = styled(Flex)`
  ${({ theme, isShadowActive }) => css`
    box-shadow: ${isShadowActive && theme.shadows.size2};
  `}
`;

const isHomePage = (pathname) => pathname === '/';

const TopbarMain = ({ gaEventCategory }) => {
  const theme = useTheme();
  const router = useRouter();
  const { pathname } = useRouter();
  const { showModal } = useModal();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const { showDrawer } = useDrawer();
  const {
    topbarMobile: { isSearchActive, isTopbarBottomActive },
    setTopbarMobileSearchActive,
  } = useTopbar();
  const [showSearchButton, setShowSearchButton] = useState(true);

  const { data: notificationCounters, isSuccess } = useQuery(
    notificationQueryKeys.counts(),
    {
      queryFn: () => get('/notification/count/types'),
      enabled: isLoggedIn,
      refetchOnWindowFocus: true,
    }
  );

  useEffect(() => {
    setShowSearchButton(!router.asPath.includes(LINKS.SEARCH));
  }, [router.asPath]);

  const onLogoClickHandler = () => {
    if (isHomePage(pathname)) {
      window.location.reload();
    }
  };

  const onHamburgerButtonClick = () =>
    showDrawer(TopbarMobileDrawer, { gaEventCategory, direction: 'left' });

  const onSearchButtonClick = () =>
    setTopbarMobileSearchActive(!isSearchActive);

  const onUnloggedInUserProfileButtonClick = () =>
    showModal(LoginModal, { isOpen: true });

  return (
    <StyledTopbarMain
      alignItems="center"
      justifyContent="space-between"
      borderBottom="1px solid"
      position="relative"
      borderColor={theme.app.topbar.borderBg}
      bg={theme.app.topbar.background}
      isShadowActive={!isTopbarBottomActive && !isSearchActive}
      py={2}
      px={4}
      zIndex={Z_INDEX_SCALE.SIZE_5}
    >
      <Flex position="relative">
        <Button
          type="ghost"
          size="size2"
          aria-label="Menu lateral"
          icon={<Icon name="menu" aria-hidden />}
          onClick={onHamburgerButtonClick}
        />
        {isSuccess && notificationCounters.countAll > 0 && (
          <StyledNotificationTag
            position="absolute"
            size="size3"
            type="alert"
            top={2}
            right={2}
            p={0}
            onClick={onHamburgerButtonClick}
          >
            <Block height="1rem" width="1rem" />
          </StyledNotificationTag>
        )}
      </Flex>
      <NextLink href={LINKS.HOME} scroll={false} prefetch={false} passHref>
        <Anchor onClick={onLogoClickHandler}>
          <Image
            alt="Promobit"
            height={33}
            width={180}
            src={`${APP.LOCAL_IMAGE_PATH}/features/topbar/promobit-logo.svg`}
            priority
          />
        </Anchor>
      </NextLink>
      <Flex>
        {showSearchButton && (
          <Button
            size="size3"
            mr={2}
            aria-label="Mostrar barra de busca"
            icon={<Icon name="search" />}
            onClick={onSearchButtonClick}
          />
        )}
        {!loadingLoggedInUser &&
          (isLoggedIn ? (
            <UserProfileButton />
          ) : (
            <Button
              size="size3"
              aria-label="Perfil de usuário"
              icon={<Icon name="user" />}
              onClick={onUnloggedInUserProfileButtonClick}
            />
          ))}
      </Flex>
    </StyledTopbarMain>
  );
};

TopbarMain.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
};

export default TopbarMain;
